import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { InputSearchDatePeriod } from '../../shared/components/input/input.search.datePeriod';
import { Filter } from '../../shared/filter/filter';
import { BasicUser, Role } from '../../shared/models/user.model';
import { InvoicePreferenceKeys, InvoiceSearchPreferences, normalizeInvoiceSearchEventForRole } from '../../shared/services/invoice-preferences.utils';
import { InvoiceSearchEvent, InvoiceService, InvoiceStatus } from '../../shared/services/invoice.service';
import { LocalStorageService } from '../../shared/services/localStorage.service';
import { UsersService } from '../../shared/services/users.service';
import { ValidationService, Validators as Vld } from '../../shared/services/validation.service';
import { UsersSelectComponent } from '../../users/users-select/users-select.component';

@Component({
  standalone: false,
  selector: 'app-invoices-search',
  templateUrl: './invoices-search.component.html',
  styleUrls: ['./invoices-search.component.scss'],
})
export class InvoicesSearchComponent implements OnInit, OnDestroy {
  @Output() submitForm = new EventEmitter<InvoiceSearchEvent>();
  @ViewChild(InputSearchDatePeriod, { static: false }) datePeriod: InputSearchDatePeriod;
  @ViewChildren(UsersSelectComponent) userSelectComponents: QueryList<UsersSelectComponent>;

  usersCollection = [];
  convertedByUsersCollection = [];
  createdByUsersCollection = [];
  isBeingDragged = false;
  isAdmin = false;
  filters: Filter[] = [
    { filterKey: 'paymentType', title: 'Type', show: true },
    { filterKey: 'createdAt', title: 'Created on', show: true },
    { filterKey: 'statuses', title: 'Status', show: true },
    { filterKey: 'minimumAmount', title: 'Minimum amount', show: true },
    { filterKey: 'packageNames', title: 'Package', show: false },
    { filterKey: 'companyNames', title: 'Company', show: false },
    { filterKey: 'userIds', title: 'Users', show: false },
    { filterKey: 'createdByUserIds', title: 'Created by', show: false },
    { filterKey: 'convertedByUserIds', title: 'Converted by', show: false },
  ];
  form: FormGroup;
  invoiceStatuses: InvoiceStatus[] = [];
  searchFormControl: FormControl = new FormControl();
  searchSubscription: Subscription;
  createdAtStart: number;
  createdAtEnd: number;

  constructor(
    private formBuilder: FormBuilder,
    private usersService: UsersService,
    private localStorage: LocalStorageService,
    public validationService: ValidationService
  ) {
    this.invoiceStatuses = InvoiceService.INVOICE_STATUSES.map(_ => {
      return { value: _, label: this.getLabelForSchedulerStatus(_), icon: this.getIconForStatus(_), isChecked: false };
    });
    this.searchSubscription = this.searchFormControl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      this.onSubmitClick();
    });
  }

  ngOnInit(): void {
    this.isAdmin = this.usersService.authUser?.role === Role.ADMIN;

    if (!this.isAdmin) {
      this.filters = this.filters.filter(filter => !this.isAdminOnlyFilter(filter.filterKey));
    }

    this.form = this.formBuilder.group({
      paymentTypes: [null],
      createdAtFrom: [null],
      createdAtTo: [null],
      statuses: [null],
      minimumAmount: [null, Vld.compose([Vld.digits(), Vld.min(0)])],
      packageNames: [null],
      companyNames: [null],
      convertedByUserIds: [null],
    });

    const existingFilters = (this.localStorage.get(InvoicePreferenceKeys.filterLayout(this.usersService.authUser.id)) || []) as Filter[];

    const allowedFilterKeys = new Set(this.filters.map(filter => filter.filterKey));
    const savedFilters = existingFilters.filter(filter => allowedFilterKeys.has(filter.filterKey));

    if (savedFilters.length === this.filters.length) {
      this.filters = savedFilters;
    } else {
      savedFilters.forEach(ef => {
        this.filters
          .filter(_ => _.filterKey === ef.filterKey)
          .forEach(f => {
            f.show = ef.show;
          });
      });
    }

    this.restoreSearchPreferences();
  }

  get paymentTypes(): string[] {
    return this.form.get('paymentTypes').value || [];
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.filters, event.previousIndex, event.currentIndex);
    this.localStorage.set(InvoicePreferenceKeys.filterLayout(this.usersService.authUser.id), this.filters);
  }

  getPeekForSelectedValues(filter): number | string | null {
    switch (filter.filterKey) {
      case 'userIds':
        return this.usersCollection.length;
      case 'createdByUserIds':
        return this.createdByUsersCollection.length;
      case 'convertedByUserIds':
        return this.convertedByUsersCollection.length;
    }
    return null;
  }

  isFilterActive(filter: Filter): boolean {
    switch (filter.filterKey) {
      case 'paymentType':
        return this.paymentTypes.length > 0;
      case 'createdAt':
        return this.form.get('createdAtFrom').value || this.form.get('createdAtTo').value;
      case 'statuses':
        return this.invoiceStatuses.filter(_ => _.isChecked).length > 0;
      case 'minimumAmount':
        return this.form.get('minimumAmount').value;
      case 'packageNames':
        return this.form.get('packageNames').value?.length > 0;
      case 'companyNames':
        return this.form.get('companyNames').value?.length > 0;
      case 'userIds':
        return this.usersCollection.length > 0;
      case 'createdByUserIds':
        return this.createdByUsersCollection.length > 0;
      case 'convertedByUserIds':
        return this.convertedByUsersCollection.length > 0;
      default:
        return false;
    }
  }

  onClear(filterKey: string) {
    switch (filterKey) {
      case 'minimumAmount':
        this.form.get('minimumAmount').reset();
        break;
      case 'createdAt':
        this.datePeriod?.reset();
        this.form.get('createdAtFrom').setValue(null);
        this.form.get('createdAtTo').setValue(null);
        this.onChangeCreatedAtDate({ startIso: null, endIso: null });
        break;
      case 'packageNames':
        this.form.get('packageNames').reset();
        break;
      case 'companyNames':
        this.form.get('companyNames').reset();
        break;
      case 'userIds':
        this.usersCollection = [];
        break;
      case 'createdByUserIds':
        this.createdByUsersCollection = [];
        break;
      case 'convertedByUserIds':
        this.convertedByUsersCollection = [];
        break;
      default:
        break;
    }
    this.onSubmitClick();
  }

  onSelectUser(user) {
    this.usersCollection.push(user);
    this.onSubmitClick();
  }

  onUnSelectUser(user, event: Event = null) {
    if (event) {
      event.stopPropagation();
    }
    this.usersCollection = this.usersCollection.filter(u => user.id !== u.id);
    this.onSubmitClick();
  }

  onSelectConvertedByUser(user) {
    this.convertedByUsersCollection.push(user);
    this.onSubmitClick();
  }

  onUnSelectConvertedByUser(user, event: Event = null) {
    if (event) {
      event.stopPropagation();
    }
    this.convertedByUsersCollection = this.convertedByUsersCollection.filter(u => user.id !== u.id);
    this.onSubmitClick();
  }

  onSelectCreatedByUser(user) {
    this.createdByUsersCollection.push(user);
    this.onSubmitClick();
  }

  onUnSelectCreatedByUser(user, event: Event = null) {
    if (event) {
      event.stopPropagation();
    }
    this.createdByUsersCollection = this.createdByUsersCollection.filter(u => user.id !== u.id);
    this.onSubmitClick();
  }

  onSubmitClick() {
    const searchData = this.getSearchData();
    const preferences: InvoiceSearchPreferences = { searchData };
    this.localStorage.set(InvoicePreferenceKeys.search(this.usersService.authUser.id), preferences);
    this.submitForm.emit(searchData);
  }

  resetAll(): void {
    this.form.reset();
    this.usersCollection = [];
    this.createdByUsersCollection = [];
    this.convertedByUsersCollection = [];
    this.userSelectComponents?.forEach(_ => _.resetAll());
    this.invoiceStatuses.forEach(_ => (_.isChecked = false));
    this.datePeriod?.reset();
    this.createdAtStart = null;
    this.createdAtEnd = null;
    this.searchFormControl.reset();
  }

  shouldAddFilterBeShown(): boolean {
    const hiddenFilters = this.filters.filter(_ => _.show === false);
    return hiddenFilters.length > 0;
  }

  deleteItem(event: CdkDragDrop<string[]>) {
    const indexToHide = event.previousIndex;
    this.filters[indexToHide].show = false;
    this.onClear(this.filters[indexToHide].filterKey);
    this.localStorage.set(InvoicePreferenceKeys.filterLayout(this.usersService.authUser.id), this.filters);
  }

  onAdditionalFilterClick(filter): void {
    filter.show = !filter.show;
    this.localStorage.set(InvoicePreferenceKeys.filterLayout(this.usersService.authUser.id), this.filters);
  }

  onClearAllFilter(): void {
    this.localStorage.set(InvoicePreferenceKeys.filterLayout(this.usersService.authUser.id), this.filters);
    this.resetAll();
    this.onSubmitClick();
  }

  onChangePaymentSelection(paymentType: string): void {
    const paymentTypeFormControl: FormControl = this.form.get('paymentTypes') as FormControl;
    const selectedPaymentTypeValue = paymentTypeFormControl.value ? paymentTypeFormControl.value : [];
    if (selectedPaymentTypeValue.includes(paymentType)) {
      paymentTypeFormControl.setValue(selectedPaymentTypeValue.filter(_ => _ !== paymentType));
    } else {
      paymentTypeFormControl.setValue([...selectedPaymentTypeValue, paymentType]);
    }
    this.onSubmitClick();
  }

  onChangeCreatedAtDate(dates): void {
    if (dates instanceof Event || typeof dates === 'string') {
      return;
    }
    const { startIso, endIso } = dates;
    this.createdAtStart = this.toUnixTimestamp(startIso);
    this.createdAtEnd = this.toUnixTimestamp(endIso);
    this.form.get('createdAtFrom').setValue(startIso);
    this.form.get('createdAtTo').setValue(endIso);
    this.onSubmitClick();
  }

  onChangeStatus(status): void {
    status.isChecked = !status.isChecked;
    this.form.get('statuses').setValue(this.invoiceStatuses.filter(_ => _.isChecked));
    this.onSubmitClick();
  }

  getLabelForSchedulerStatus(status: string): string {
    return status[0] + status.slice(1).toLowerCase();
  }

  getIconForStatus(status: string): string {
    switch (status) {
      case 'WAITING':
        return 'icon-wait fs-4';
      case 'PROCESSING':
        return 'icon-wait fs-4 text-warning-w2';
      case 'CONFIRMED':
        return 'icon-positive text-success-s2 fs-4';
      case 'PARTIALLY_PAID':
        return 'icon-check text-warning-w2 fs-5';
      case 'VOID':
        return 'icon-close text-error-r3 fs-5';
      default:
        return '';
    }
  }

  private isAdminOnlyFilter(filterKey: string): boolean {
    return ['paymentType', 'companyNames', 'userIds', 'convertedByUserIds'].includes(filterKey);
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  private getSearchData(): InvoiceSearchEvent {
    const searchData: InvoiceSearchEvent = this.form.getRawValue();
    searchData.statuses = this.invoiceStatuses.filter(_ => _.isChecked).map(_ => _.value);
    searchData.companyNames = this.parseCommaSeparatedValues(this.form.get('companyNames').value);
    searchData.packageNames = this.parseCommaSeparatedValues(this.form.get('packageNames').value);
    searchData.userIds = this.usersCollection.map(_ => _.id);
    searchData.convertedByUserIds = this.convertedByUsersCollection.map(_ => _.id);
    searchData.createdByUserIds = this.createdByUsersCollection.map(_ => _.id);
    searchData.searchString = this.searchFormControl.value;
    return searchData;
  }

  private restoreSearchPreferences(): void {
    const preferences = this.localStorage.get(InvoicePreferenceKeys.search(this.usersService.authUser.id)) as InvoiceSearchPreferences;
    if (!preferences?.searchData) {
      return;
    }

    const searchData = normalizeInvoiceSearchEventForRole(preferences.searchData, this.usersService.authUser.role);
    this.form.patchValue(
      {
        paymentTypes: searchData.paymentTypes,
        createdAtFrom: searchData.createdAtFrom,
        createdAtTo: searchData.createdAtTo,
        minimumAmount: searchData.minimumAmount,
        packageNames: searchData.packageNames?.length ? searchData.packageNames.join(', ') : null,
        companyNames: searchData.companyNames?.length ? searchData.companyNames.join(', ') : null,
      },
      { emitEvent: false }
    );
    this.invoiceStatuses.forEach(status => (status.isChecked = searchData.statuses?.includes(status.value)));
    this.form.get('statuses').setValue(
      this.invoiceStatuses.filter(status => status.isChecked),
      { emitEvent: false }
    );
    this.searchFormControl.setValue(searchData.searchString || null, { emitEvent: false });
    this.usersCollection = this.restoreUsersCollection(searchData.userIds);
    this.createdByUsersCollection = this.restoreUsersCollection(searchData.createdByUserIds);
    this.convertedByUsersCollection = this.restoreUsersCollection(searchData.convertedByUserIds);
    this.createdAtStart = this.toUnixTimestamp(searchData.createdAtFrom);
    this.createdAtEnd = this.toUnixTimestamp(searchData.createdAtTo);
  }

  private restoreUsersCollection(savedIds: number[] = []): BasicUser[] {
    return savedIds.map(id => ({ id }) as BasicUser);
  }

  private toUnixTimestamp(date: string): number {
    const timestamp = date ? new Date(date).getTime() : NaN;
    return Number.isNaN(timestamp) ? null : Math.floor(timestamp / 1000);
  }

  private parseCommaSeparatedValues(value: string): string[] {
    return value
      ? value
          .split(',')
          .map(item => item.trim())
          .filter(Boolean)
      : [];
  }
}
